import React, { useState } from "react";

import {
  faCheck,
  faChevronRight,
  faInfoCircle,
  faRefresh,
} from "@fortawesome/pro-regular-svg-icons";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  MenuSeparator,
} from "@headlessui/react";
import { Link } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { OrgLogo } from "src/base-components/OrgLogo";
import { Pill } from "src/base-components/Pill";
import { WorkspaceDataplane } from "src/clients/flow-api";
import { Avatar } from "src/design-system/Avatar";
import { Icon } from "src/design-system/Icon";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { useIsFeatureFlagEnabled } from "src/hooks/useIsFeatureFlagEnabled";
import { OrgWithWorkspaces } from "src/layout/types";
import { FEATURE_FLAGS } from "src/router/featureFlags";
import {
  adminPanelPath,
  getSignUpUrlWithNext,
  getUrlToWsDashboard,
} from "src/router/urls";
import {
  useAuthActions,
  useIsFWDOrAdmin,
  useUserData,
} from "src/store/AuthStore";
import { copyTextToClipboard } from "src/utils/clipboard";
import { useNavigableWorkspaceAndOrg } from "src/utils/useCurrentWorkspace";

const Divider = <MenuSeparator className="h-px bg-gray-100" />;

const UserInformationDisplay = () => {
  const {
    signed_in_full_name,
    signed_in_username,
    signed_in_avatar_url,
    signed_in_email,
  } = useUserData();
  return (
    <div className="flex items-center gap-x-2 px-4 pt-2.5 pb-3.5">
      <Avatar
        size="lg"
        user={{
          full_name: signed_in_full_name,
          avatar_url: signed_in_avatar_url,
          username: signed_in_username,
        }}
      />
      <div>
        <div className="font-inter-semibold-13px text-gray-800">
          {signed_in_full_name ?? signed_in_username}
        </div>
        <div className="font-inter-normal-12px text-gray-600">
          {signed_in_email ?? signed_in_username}
        </div>
      </div>
    </div>
  );
};

const ActiveOrgItem: React.FC<{
  icon: React.ReactNode;
  name: string;
  principal: string;
  children: React.ReactNode;
}> = ({ icon, name, principal, children }) => {
  return (
    <>
      <div className="sticky top-0 bg-white">
        <MenuItem disabled>
          <div className="flex items-center gap-x-2 px-4 py-2.5">
            {icon}
            <span className="mr-auto overflow-hidden break-words font-inter-medium-13px text-gray-800">
              {name}
            </span>
            <Tooltip
              body={
                <div>
                  Organization ID:{" "}
                  <Pill size="sm" variant="gray">
                    <Pill.Text>{principal}</Pill.Text>
                  </Pill>
                </div>
              }
              placement="top"
              title={undefined}
              asChild
            >
              <Icon icon={faInfoCircle} size="2xs" />
            </Tooltip>
          </div>
        </MenuItem>
      </div>
      <div className="ml-7.5 flex flex-row">
        <span className="my-2 w-0.5 bg-gray-200" />
        <div className="w-full flex-grow overflow-hidden">{children}</div>
      </div>
      <div className="h-3" />
    </>
  );
};

const WorkspaceItem: React.FC<{
  name: string;
  selected?: boolean;
  truncate?: boolean;
  onClick?: () => void;
}> = ({ name, selected, truncate, onClick }) => {
  return (
    <MenuItem>
      <div
        className={twJoin(
          "flex items-center gap-x-1.5 overflow-hidden px-4 py-3.5",
          "data-[focus]:bg-gray-50",
          onClick ? "cursor-copy" : "cursor-pointer",
        )}
        onClick={onClick}
      >
        <span
          className={twJoin(
            "mr-auto overflow-hidden font-inter-normal-13px text-gray-800",
            truncate ? "truncate" : "break-words",
          )}
        >
          {name}
        </span>
        {selected && <Icon icon={faCheck} size="2xs" variant="active" />}
      </div>
    </MenuItem>
  );
};

const Powertools: React.FC = () => {
  const { signed_in_user_id, api_key } = useUserData();

  const handleClick = (value: string | undefined, message: string) => {
    if (value) {
      copyTextToClipboard(value);
      toastActions.success({ title: message });
    }
  };

  return (
    <>
      {Divider}
      <ActiveOrgItem
        icon={<span>⚡</span>}
        name="Powertools"
        principal="Powertools"
      >
        <WorkspaceItem
          name={`📝 gitsha ${window.environment.GITHUB_SHA ?? "NOT SET"}`}
          truncate
          onClick={() =>
            handleClick(
              window.environment.GITHUB_SHA,
              "Copied GITHUB_SHA to clipboard!",
            )
          }
        />
        <WorkspaceItem name={`🌄 env ${window.environment.ENV}`} truncate />
        <WorkspaceItem
          name={`👤 ${signed_in_user_id}`}
          truncate
          onClick={() =>
            handleClick(signed_in_user_id, "Copied user_id to clipboard!")
          }
        />
        <WorkspaceItem
          name="🔑 xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          truncate
          onClick={() => handleClick(api_key, "Copied api_key to clipboard!")}
        />
      </ActiveOrgItem>
    </>
  );
};

const LinkToAdminPanel: React.FC = () => {
  const isFWDOrAdmin = useIsFWDOrAdmin();

  if (!isFWDOrAdmin) {
    return null;
  }

  return (
    <MenuItem>
      <Link
        key="link-to-admin-panel"
        className="flex items-center gap-x-1.5 px-4 py-3.5 font-inter-normal-13px text-gray-800 hover:cursor-pointer hover:bg-gray-50"
        to={adminPanelPath}
      >
        Admin panel
      </Link>
    </MenuItem>
  );
};

const USER_MENU_VARIANTS = {
  header: {
    avatarSize: "lg" as const,
    containerClass: "h-8",
    buttonClass: "h-8 flex items-center gap-x-2 justify-start w-full",
    showUsername: false,
    anchorTo: "bottom end" as const,
    avatarPadding: false,
    menuItemsClass: "",
    gap: 8,
  },
  sidebar: {
    avatarSize: "sm" as const,
    containerClass: "h-5 w-full",
    buttonClass: "flex items-center h-5 gap-x-2 justify-start w-full",
    showUsername: true,
    anchorTo: "right start" as const,
    avatarPadding: true,
    menuItemsClass: "[--anchor-max-height:60%]",
    gap: 18,
  },
} as const;

export type UserMenuProps = {
  variant?: keyof typeof USER_MENU_VARIANTS;
  showUsername?: boolean;
};

export const UserMenu: React.FC<UserMenuProps> = ({
  variant = "header",
  showUsername: showUsernameOverride,
}) => {
  const config = USER_MENU_VARIANTS[variant];
  const showUsername = showUsernameOverride ?? config.showUsername;
  const powertoolsEnabled = useIsFeatureFlagEnabled(FEATURE_FLAGS.powertools);

  const { org, workspace, orgs } = useNavigableWorkspaceAndOrg();
  const [mode, setMode] = useState<"org" | "ws">("ws");
  const { signed_in_full_name, signed_in_username, signed_in_avatar_url } =
    useUserData();

  const { resetAll } = useAuthActions();

  const logout = () => {
    resetAll();
    window.location.assign(getSignUpUrlWithNext());
  };

  if (!org || !workspace) {
    return undefined;
  }

  return (
    <Menu as="div" className={config.containerClass}>
      {({ close }) => (
        <>
          <MenuButton className={config.buttonClass}>
            <div className={config.avatarPadding ? "p-px" : ""}>
              <Avatar
                size={config.avatarSize}
                user={{
                  full_name: signed_in_full_name,
                  avatar_url: signed_in_avatar_url,
                  username: signed_in_username,
                }}
              />
            </div>
            {showUsername && (
              <span className="truncate font-inter-medium-13px text-gray-800">
                {signed_in_full_name ?? signed_in_username}
              </span>
            )}
          </MenuButton>
          <MenuItems
            anchor={{
              to: config.anchorTo,
              gap: config.gap,
              padding: 12,
            }}
            className={twJoin(
              "z-50 flex w-[248px] flex-col overflow-y-auto rounded-lg bg-white py-2 shadow-lg ring-1 ring-gray-200 ring-opacity-5 focus:outline-none",
              "transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0",
              config.menuItemsClass,
            )}
            transition
            onTransitionEnd={() => {
              if (mode === "org") {
                setMode("ws");
              }
            }}
          >
            {mode === "org" && (
              <OrgPicker createOnClick={() => () => {}} orgs={orgs} />
            )}
            {mode === "ws" && (
              <>
                <UserInformationDisplay />
                {Divider}
                <div className="decideScrollbarThin min-h-[48px] flex-shrink overflow-auto">
                  <ActiveOrgItem
                    icon={<OrgLogo org={org} size="medium" />}
                    name={org.name}
                    principal={org.principal}
                  >
                    {org.workspaces.map((ws) => (
                      <Link
                        key={ws.id}
                        to={getUrlToWsDashboard({
                          orgId: ws.organization_id,
                          wsId: ws.id,
                        })}
                        onClick={close}
                      >
                        <WorkspaceItem
                          name={ws.name}
                          selected={ws.id === workspace.id}
                        />
                      </Link>
                    ))}
                  </ActiveOrgItem>

                  {powertoolsEnabled && <Powertools />}
                </div>

                {Divider}

                {orgs.length > 1 && (
                  <>
                    <MenuItem>
                      <div
                        className={twJoin(
                          "flex h-12 items-center gap-x-1.5 px-4 py-3",
                          "data-[focus]:cursor-pointer data-[focus]:bg-gray-50",
                        )}
                        onClick={(e) => {
                          e.preventDefault();
                          setMode("org");
                        }}
                      >
                        <Icon icon={faRefresh} size="xs" />
                        <span className="font-inter-normal-13px text-gray-800">
                          Switch organization
                        </span>
                      </div>
                    </MenuItem>
                    {Divider}
                  </>
                )}

                <LinkToAdminPanel />

                <MenuItem>
                  <div
                    className={twJoin(
                      "flex items-center gap-x-1.5 px-4 py-3.5 font-inter-normal-13px text-gray-800",
                      "data-[focus]:cursor-pointer data-[focus]:bg-gray-50",
                    )}
                    onClick={logout}
                  >
                    Sign out
                  </div>
                </MenuItem>
              </>
            )}
          </MenuItems>
        </>
      )}
    </Menu>
  );
};

const OrgPicker: React.FC<{
  orgs: OrgWithWorkspaces[];
  createOnClick: (
    org: OrgWithWorkspaces,
  ) => React.MouseEventHandler<HTMLDivElement>;
}> = ({ orgs, createOnClick }) => {
  return orgs.map((org) => {
    const firstWorkspace: WorkspaceDataplane | undefined = org.workspaces[0];
    const item = (
      <DropdownItem
        key={org.id}
        label={org.name}
        logo={<OrgLogo org={org} size="small" />}
        link
        onClick={firstWorkspace ? undefined : createOnClick(org)}
      />
    );

    return firstWorkspace ? (
      <Link
        key={org.id}
        to={getUrlToWsDashboard({ orgId: org.id, wsId: firstWorkspace.id })}
      >
        {item}
      </Link>
    ) : (
      item
    );
  });
};

type Props = {
  logo?: React.ReactNode;
  label: string;
  selected?: boolean;
  link?: boolean;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
};

const DropdownItem: React.FC<Props> = ({
  logo,
  label,
  disabled = false,
  selected = false,
  link = false,
  onClick,
}) => (
  <MenuItem disabled={disabled || selected}>
    <div
      className={twJoin(
        "flex items-center gap-x-1.5 px-4 py-2",
        (link || !logo) &&
          "data-[focus]:cursor-pointer data-[focus]:bg-gray-50",
      )}
      onClick={onClick}
    >
      {logo}
      <span className="mr-auto overflow-hidden break-words font-inter-semibold-13px text-gray-800">
        {label}
      </span>
      {selected && <Icon icon={faCheck} size="2xs" variant="active" />}
      {link && <Icon icon={faChevronRight} size="2xs" />}
    </div>
  </MenuItem>
);
